// ProductoFormXL - Sprint 44 Fase 3 Vista 3.
// Form de producto XL. Reemplaza src/modules/productos/shared/ProductoForm.jsx
// (monolito 502L). Layout: header con Back/titulo/estado chip/acciones, tabs
// en XLChip, body dividido en tabs + sidebar resumen a la derecha.
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { ensureKeyframes, injectCSS } from '../../design/motion';
import useProductoFormXL from './hooks/useProductoFormXL';
import { TABS } from './utils';
import XLBadge from '../../components/xl/XLBadge';
import XLChip from '../../components/xl/XLChip';
import XLSkeleton from '../../components/xl/XLSkeleton';
import BasicosTabXL from './tabs/BasicosTabXL';
import TecnicaTabXL from './tabs/TecnicaTabXL';
import PreciosTabXL from './tabs/PreciosTabXL';
import AuditoriaTabXL from './tabs/AuditoriaTabXL';
import ResumenSidebar from './components/ResumenSidebar';

const CSS_ID = 'xl-prod-form-css';
const CSS = `
.xl-page { padding: 24px; font-family: var(--xl-font-sans); color: var(--xl-color-text) }
.xl-prod-hd {
  display: flex; align-items: flex-start; justify-content: space-between;
  gap: 16px; margin-bottom: 16px; flex-wrap: wrap;
}
.xl-prod-hd__left { display: flex; align-items: center; gap: 14px; min-width: 0 }
.xl-prod-hd__back {
  font: 500 13px/1 var(--xl-font-sans);
  color: var(--xl-color-text-secondary);
  background: transparent; border: none; cursor: pointer;
  padding: 6px 8px; border-radius: var(--xl-radius-md);
}
.xl-prod-hd__back:hover { background: var(--xl-color-surface-alt); color: var(--xl-color-text) }
.xl-prod-hd__back:focus-visible { outline: 2px solid var(--xl-color-focus); outline-offset: 2px }
.xl-prod-hd__title { margin: 0; font-size: 20px; font-weight: 600; line-height: 1.2 }
.xl-prod-hd__sub { margin: 2px 0 0; font-size: 12px; color: var(--xl-color-text-secondary) }
.xl-prod-hd__mono { font-family: var(--xl-font-mono); font-size: 13px; color: var(--xl-color-text-secondary) }
.xl-prod-hd__actions { display: flex; gap: 8px; align-items: center }
.xl-btn {
  font: 500 13px/1 var(--xl-font-sans); padding: 10px 16px;
  border-radius: var(--xl-radius-md); border: 1px solid transparent; cursor: pointer;
  transition: background 120ms cubic-bezier(0,0,0.2,1), border-color 120ms cubic-bezier(0,0,0.2,1);
}
.xl-btn:focus-visible { outline: 2px solid var(--xl-color-focus); outline-offset: 2px }
.xl-btn--primary { background: var(--xl-color-primary); color: var(--xl-color-text-inverse) }
.xl-btn--primary:hover { background: var(--xl-color-primary-hover) }
.xl-btn--ghost {
  background: transparent; color: var(--xl-color-text-secondary);
  border-color: var(--xl-color-border);
}
.xl-btn--ghost:hover { background: var(--xl-color-surface); color: var(--xl-color-text) }
.xl-btn--danger {
  background: transparent; color: var(--xl-color-danger);
  border-color: rgba(220,38,38,0.35);
}
.xl-btn--danger:hover { background: rgba(220,38,38,0.08) }
.xl-btn:disabled { opacity: 0.45; cursor: not-allowed }

.xl-banner { padding: 10px 14px; margin-bottom: 12px; border-radius: var(--xl-radius-md); font-size: 13px }
.xl-banner--ok  { background: rgba(5,150,105,0.10); color: var(--xl-color-success); border: 1px solid rgba(5,150,105,0.28) }
.xl-banner--err { background: rgba(220,38,38,0.10); color: var(--xl-color-danger);  border: 1px solid rgba(220,38,38,0.28) }

.xl-prod-layout { display: flex; gap: 16px; align-items: flex-start }
@media (max-width: 1100px) { .xl-prod-layout { flex-direction: column } }

.xl-prod-main {
  flex: 1; min-width: 0;
  background: var(--xl-color-surface);
  border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-lg);
  box-shadow: var(--xl-shadow-sm);
  overflow: hidden;
}
.xl-prod-tabs {
  display: flex; gap: 6px; flex-wrap: wrap;
  padding: 12px 16px;
  border-bottom: 1px solid var(--xl-color-border);
  background: var(--xl-color-bg);
}
.xl-prod-body { padding: 20px }
.xl-prod-loading { padding: 20px; display: flex; flex-direction: column; gap: 10px }
`;

export default function ProductoFormXL() {
  useEffect(() => { ensureKeyframes(); injectCSS(CSS_ID, CSS); }, []);

  const { id } = useParams();
  const productoId = id ? Number(id) : null;
  const fm = useProductoFormXL(productoId);

  if (fm.loading) {
    return (
      <div className="xl-page">
        <div className="xl-prod-loading" aria-busy="true">
          <XLSkeleton variant="line" width="30%" height={18} />
          <XLSkeleton variant="rect" height={140} />
          <XLSkeleton variant="rect" height={220} />
        </div>
      </div>
    );
  }

  const titulo = fm.isEdit
    ? ('Editar Producto #' + productoId)
    : 'Nuevo Producto';
  const subtitulo = fm.isEdit ? 'Editar producto' : 'Registrar producto';

  return (
    <div className="xl-page">
      <header className="xl-prod-hd">
        <div className="xl-prod-hd__left">
          <button
            type="button"
            className="xl-prod-hd__back"
            onClick={() => fm.navigate('/productos')}
          >
            {'\u2190 Volver'}
          </button>
          <div>
            <h2 className="xl-prod-hd__title">{titulo}</h2>
            <p className="xl-prod-hd__sub">{subtitulo}</p>
          </div>
          {fm.isEdit && (
            <XLBadge variant={fm.form.is_active ? 'success' : 'neutral'} dot>
              {fm.form.is_active ? 'Activo' : 'Inactivo'}
            </XLBadge>
          )}
          {fm.form.codigo && (
            <span className="xl-prod-hd__mono">{fm.form.codigo}</span>
          )}
        </div>
        <div className="xl-prod-hd__actions">
          {fm.isEdit && (
            <button
              type="button"
              className={'xl-btn ' + (fm.form.is_active ? 'xl-btn--danger' : 'xl-btn--ghost')}
              onClick={fm.onToggleActivo}
              disabled={fm.acting || fm.saving}
            >
              {fm.acting ? 'Procesando\u2026' : (fm.form.is_active ? 'Inactivar' : 'Activar')}
            </button>
          )}
          <button
            type="button"
            className="xl-btn xl-btn--primary"
            onClick={fm.onSave}
            disabled={fm.saving}
          >
            {fm.saving ? 'Guardando\u2026' : (fm.isEdit ? 'Guardar' : 'Crear')}
          </button>
        </div>
      </header>

      {fm.msg && (
        <div
          className={'xl-banner ' + (fm.msg.kind === 'ok' ? 'xl-banner--ok' : 'xl-banner--err')}
          role={fm.msg.kind === 'ok' ? 'status' : 'alert'}
          onClick={fm.clearMsg}
        >
          {fm.msg.text}
        </div>
      )}

      <div className="xl-prod-layout">
        <div className="xl-prod-main">
          <div className="xl-prod-tabs" role="tablist" aria-label="Secciones del producto">
            {TABS.map((t) => (
              <XLChip
                key={t.id}
                onClick={() => fm.setTab(t.id)}
                selected={fm.tab === t.id}
              >
                {t.label}
              </XLChip>
            ))}
          </div>
          <div className="xl-prod-body">
            {fm.tab === 'basicos' && (
              <BasicosTabXL
                form={fm.form} set={fm.set} errors={fm.errors}
                productoId={productoId} uploading={fm.uploading}
                onUploadImage={fm.onUploadImage} onDeleteImage={fm.onDeleteImage}
                categorias={fm.categorias}
              />
            )}
            {fm.tab === 'tecnica' && (
              <TecnicaTabXL form={fm.form} set={fm.set} errors={fm.errors} />
            )}
            {fm.tab === 'precios' && (
              <PreciosTabXL form={fm.form} set={fm.set} errors={fm.errors} />
            )}
            {fm.tab === 'auditoria' && (
              <AuditoriaTabXL form={fm.form} isEdit={fm.isEdit} />
            )}
          </div>
        </div>

        <ResumenSidebar form={fm.form} />
      </div>
    </div>
  );
}
